import cx from 'classnames'
import { useTranslation } from 'react-i18next'

import { Message, User } from 'lib/_types'
import { useCurrentUser } from 'store/api/yay/user'

import Avatar from 'components/Avatar/Avatar'
import { ChatMessageReplyPreviewMedia } from './ChatMessageReplyPreviewMedia'
import { ChatMessageReplyPreviewText } from './ChatMessageReplyPreviewText'

import './style.scss'

type Props = {
  message: Message
  messageOwner?: User
  parentMessageOwner?: User
  onJumpToMessage: (id: number) => void
}

export const ChatMessageReplyPreview = ({
  message,
  messageOwner,
  parentMessageOwner,
  onJumpToMessage,
}: Props) => {
  if (!message?.parent_id) return null

  const { t } = useTranslation()
  const { data: currentUser } = useCurrentUser()

  const isCurrentUserMessage = messageOwner?.id === currentUser?.id
  const isParentCurrentUserMessage = parentMessageOwner?.id === currentUser?.id

  return (
    <div
      className={cx('ChatMessageReplyPreview', {
        'ChatMessageReplyPreview--self-side': isCurrentUserMessage,
      })}
    >
      <div className="ChatMessageReplyPreview__bar"></div>

      <div
        className={cx('ChatMessageReplyPreview__body', {
          'ChatMessageReplyPreview__body--self-message':
            isParentCurrentUserMessage,
        })}
        onClick={() => {
          onJumpToMessage(message.parent_id)
        }}
      >
        <div className="ChatMessageReplyPreview__body__user">
          {parentMessageOwner ? (
            <>
              <Avatar
                className="ChatMessageReplyPreview__body__user__avatar"
                user={parentMessageOwner}
              />
              <span className="ChatMessageReplyPreview__body__user__name">
                {isParentCurrentUserMessage
                  ? t('chat.message.reply.you')
                  : parentMessageOwner.nickname}
              </span>
            </>
          ) : (
            <span className="ChatMessageReplyPreview__body__user__name">
              {t('chat.message.reply.deleted_user')}
            </span>
          )}
        </div>

        <div
          className={cx('ChatMessageReplyPreview__body__content', {
            [`ChatMessageReplyPreview__body__content--${message.parent?.message_type}`]:
              message.parent?.message_type,
          })}
        >
          {(() => {
            switch (message.parent?.message_type) {
              case 'deleted':
              case undefined:
                return (
                  <ChatMessageReplyPreviewText iconPath="/images/icon-delete-red.svg">
                    {t('chat.message.reply.deleted_message')}
                  </ChatMessageReplyPreviewText>
                )

              case 'text':
                return (
                  <ChatMessageReplyPreviewText>
                    {message.parent?.text}
                  </ChatMessageReplyPreviewText>
                )

              case 'gif':
              case 'sticker':
              case 'image':
              case 'eternal_image':
              case 'video':
              case 'eternal_video':
                return (
                  <ChatMessageReplyPreviewMedia
                    message={message.parent}
                    isCurrentUserMessage={isParentCurrentUserMessage}
                  />
                )

              default:
                return null
            }
          })()}
        </div>
      </div>
    </div>
  )
}
